// Универсальный сервис уведомлений
// Поддерживает Web Push API и Firebase Cloud Messaging
import pushNotificationService from './pushNotificationService';
import fcmService from './fcmService';


export type NotificationMethod = 'web-push' | 'fcm' | 'both' | 'none';

interface NotificationSupport {
  webPush: boolean;
  fcm: boolean;
  method: NotificationMethod;
}

class UniversalNotificationService {
  private support: NotificationSupport = {
    webPush: false,
    fcm: false,
    method: 'none'
  };

  private readyPromise: Promise<void> | null = null;

  constructor() {
    this.checkSupport();
  }

  /** Wait for Firebase init, then re-evaluate support. Safe to call often. */
  ready(): Promise<void> {
    if (!this.readyPromise) {
      this.readyPromise = fcmService.ready.then(() => this.checkSupport());
    }
    return this.readyPromise;
  }

  // Проверка поддержки различных методов уведомлений
  private checkSupport() {
    try {
      // Web Push (own VAPID) is not configured on the server — FCM only.
      this.support.webPush = false;
      
      // Проверяем FCM
      this.support.fcm = fcmService.isSupported();
      
      // Определяем лучший метод
      if (this.support.webPush && this.support.fcm) {
        this.support.method = 'both';
      } else if (this.support.fcm) {
        this.support.method = 'fcm';
      } else if (this.support.webPush) {
        this.support.method = 'web-push';
      } else {
        this.support.method = 'none';
      }

      console.log('Notification support check:', this.support);
    } catch (error) {
      console.warn('Error checking notification support:', error);
      this.support.method = 'none';
    }
  }

  // Получение информации о поддержке
  getSupport(): NotificationSupport {
    return { ...this.support };
  }

  // Проверка, поддерживаются ли уведомления вообще
  isSupported(): boolean {
    return this.support.method !== 'none';
  }

  // Получение лучшего доступного метода
  getBestMethod(): NotificationMethod {
    return this.support.method;
  }

  // Подписка на уведомления (автоматически выбирает лучший метод)
  async subscribe(): Promise<boolean> {
    try {
      await this.ready();
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
      if (this.support.method === 'none') {
        throw new Error('No notification method supported');
      }

      let success = false;

      // Пробуем FCM сначала (лучше работает на мобильных)
      if (this.support.fcm) {
        try {
          console.log('🔍 Пробуем FCM подписку...');
          const token = await fcmService.getToken();
          if (token) {
            console.log('✅ FCM токен получен, отправляем на сервер...');
            const sentToServer = await fcmService.sendTokenToServer(token);
            if (sentToServer) {
              success = true;
              console.log('✅ FCM подписка успешна');
            } else {
              console.warn('⚠️ FCM токен не отправлен на сервер, пробуем Web Push');
            }
          } else {
            console.warn('⚠️ FCM токен не получен, пробуем Web Push');
          }
        } catch (error) {
          console.warn('⚠️ FCM подписка не удалась, пробуем Web Push:', error);
        }
      }

      // Если FCM не сработал или не поддерживается, пробуем Web Push
      if (!success && this.support.webPush) {
        try {
          const permission = await pushNotificationService.requestPermission();
          if (permission === 'granted') {
            const subscription = await pushNotificationService.subscribe();
            success = !!subscription;
            if (success) {
              console.log('Web Push subscription successful');
            }
          }
        } catch (error) {
          console.warn('Web Push subscription failed:', error);
        }
      }

      return success;
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      return false;
    }
  }

  // Отписка от уведомлений
  async unsubscribe(): Promise<boolean> {
    try {
      let success = false;

      // Отписываемся от FCM
      if (this.support.fcm) {
        try {
          const token = fcmService.getStoredToken();
          if (token) {
            await fcmService.removeTokenFromServer(token);
            await fcmService.deleteToken();
            success = true;
          }
        } catch (error) {
          console.warn('FCM unsubscribe failed:', error);
        }
      }

      // Отписываемся от Web Push
      if (this.support.webPush) {
        try {
          const webPushSuccess = await pushNotificationService.unsubscribe();
          success = success || webPushSuccess;
        } catch (error) {
          console.warn('Web Push unsubscribe failed:', error);
        }
      }

      return success;
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      return false;
    }
  }

  // Проверка статуса подписки
  async getSubscriptionStatus(): Promise<boolean> {
    try {
      await this.ready();
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return false;
      let hasSubscription = false;

      // Проверяем FCM
      if (this.support.fcm) {
        hasSubscription = hasSubscription || fcmService.hasStoredToken();
      }

      // Проверяем Web Push
      if (this.support.webPush) {
        try {
          const webPushStatus = await pushNotificationService.getSubscriptionStatus();
          hasSubscription = hasSubscription || webPushStatus;
        } catch (error) {
          console.warn('Error checking Web Push status:', error);
        }
      }

      return hasSubscription;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  // Настройка слушателя входящих сообщений
  setupMessageListener(callback: (payload: any) => void): (() => void) | null {
    try {
      // FCM поддерживает активные сообщения
      if (this.support.fcm) {
        return fcmService.onMessage(callback);
      }
      
      // Web Push API не поддерживает активные сообщения
      return null;
    } catch (error) {
      console.error('Error setting up message listener:', error);
      return null;
    }
  }

  // Получение информации о платформе
  getPlatformInfo(): { isMobile: boolean; isIOS: boolean; isAndroid: boolean; isSafari: boolean } {
    try {
      const userAgent = navigator?.userAgent || '';
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

      return { isMobile, isIOS, isAndroid, isSafari };
    } catch (error) {
      console.warn('Error getting platform info:', error);
      return { isMobile: false, isIOS: false, isAndroid: false, isSafari: false };
    }
  }

  // Получение рекомендации по методу уведомлений для текущей платформы
  getRecommendedMethod(): NotificationMethod {
    try {
      const platform = this.getPlatformInfo();
      
      // На iOS лучше использовать FCM
      if (platform.isIOS) {
        return this.support.fcm ? 'fcm' : 'none';
      }
      
      // На Android FCM работает лучше
      if (platform.isAndroid) {
        return this.support.fcm ? 'fcm' : (this.support.webPush ? 'web-push' : 'none');
      }
      
      // На Safari (десктоп) Web Push API не поддерживается
      if (platform.isSafari && !platform.isMobile) {
        return this.support.fcm ? 'fcm' : 'none';
      }
      
      // В остальных случаях используем доступный метод
      return this.support.method;
    } catch (error) {
      console.warn('Error getting recommended method:', error);
      return this.support.method;
    }
  }
}

export const universalNotificationService = new UniversalNotificationService();
export default universalNotificationService;
